/* Rulnx storefront enhancements; Shopify remains the authority for price and stock. */
(() => {
  const boundProducts = new WeakSet();
  const dialogTriggers = new WeakMap();
  const root = window.Shopify?.routes?.root || '/';
  const route = (key, fallback) => window.routes?.[key] || root + fallback;
  let headerFrame = 0;
  function updateHeader() {
    headerFrame = 0;
    const header = document.querySelector('.section-header');
    if (header) document.documentElement.style.setProperty('--rx-header', `${Math.round(header.getBoundingClientRect().height)}px`);
    document.body.classList.remove('rx-header-over-hero');
  }
  function scheduleHeader() { if (!headerFrame) headerFrame = requestAnimationFrame(updateHeader); }
  window.addEventListener('scroll', scheduleHeader, { passive: true });
  window.addEventListener('resize', scheduleHeader, { passive: true });

  function openDialog(dialog, trigger) {
    if (dialog && typeof dialog.show === 'function' && !(dialog instanceof HTMLDialogElement)) { dialog.show(trigger); return; }
    if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;
    dialogTriggers.set(dialog, trigger);
    dialog.showModal();
    document.body.classList.add('rx-dialog-open');
    dialog.querySelector('[data-rx-close]')?.focus();
    dialog.addEventListener('close', () => {
      if (!document.querySelector('.rx-dialog[open]')) document.body.classList.remove('rx-dialog-open');
      dialogTriggers.get(dialog)?.focus({ preventScroll: true });
    }, { once: true });
  }
  function closeDialog(dialog) {
    if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
  }
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-rx-open]');
    if (trigger) {
      if (trigger.closest('summary')) event.preventDefault();
      openDialog(document.getElementById(trigger.dataset.rxOpen), trigger);
    }
    const closer = event.target.closest('[data-rx-close]');
    if (closer) closeDialog(closer.closest('dialog'));
    if (event.target instanceof HTMLDialogElement) {
      const box = event.target.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) closeDialog(event.target);
    }
  });
  document.addEventListener('keyup', event => {
    if (event.key !== 'Escape' || event.target !== document.body) return;
    const drawer = document.querySelector('header-drawer');
    const opened = drawer?.querySelector(':scope > details[open]');
    if (opened) drawer.closeMenuDrawer(event, opened.querySelector(':scope > summary'));
  });
  document.addEventListener('toggle', event => {
    if (event.target.matches('[data-option-disclosure][open]')) {
      const product = event.target.closest('product-info');
      product?.querySelectorAll('[data-option-disclosure][open]').forEach(row => { if (row !== event.target) row.open = false; });
    }
  }, true);

  document.addEventListener('submit', async event => {
    const form = event.target.closest('[data-rx-quick-form]');
    if (!form) return;
    event.preventDefault();
    if (form.dataset.busy === 'true' || !form.reportValidity()) return;
    const button = form.querySelector('[type="submit"]');
    const status = form.querySelector('.rx-form-status');
    const drawer = document.querySelector('cart-drawer');
    const original = button.textContent;
    const body = new FormData(form);
    if (!body.get('id')) return;
    if (drawer?.getSectionsToRender) {
      body.append('sections', drawer.getSectionsToRender().map(section => section.id).join(','));
      body.append('sections_url', window.location.pathname);
      drawer.setActiveElement?.(window.CatalogueQuickShop?.returnFocus?.(form) || window.CompanionSheet?.returnFocus?.(form) || form.closest('catalogue-card')?.querySelector('.rx-card__toggle') || button);
    }
    form.dataset.busy = 'true'; button.disabled = true; button.textContent = 'Adding…'; status.textContent = '';
    let confirmed = false;
    try {
      const response = await fetch(route('cart_add_url', 'cart/add.js'), { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body });
      const data = await response.json();
      if (!response.ok || data.status) {
        status.textContent = typeof data.description === 'string' ? data.description : 'That selection could not be added. Please check the size and try again.';
        return;
      }
      confirmed = true;
      status.textContent = 'Added to your bag.';
      form.dispatchEvent(new CustomEvent('quick-add:success', {bubbles:true}));
      form.closest('dialog')?.close();
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'rulnx-quick-add', productVariantId: body.get('id'), cartData: data });
      if (drawer?.renderContents && data.sections) {
        drawer.classList.remove('is-empty');
        drawer.renderContents(data);
      } else window.location.assign(route('cart_url', 'cart'));
    } catch (error) {
      status.textContent = confirmed ? 'Added to your bag. ' : 'We could not confirm the bag update. ';
      const link = document.createElement('a'); link.href = route('cart_url', 'cart');
      link.textContent = confirmed ? 'View bag' : 'Check your bag before trying again'; status.append(link);
    } finally {
      form.dataset.busy = 'false'; button.disabled = false; button.textContent = original;
    }
  });

  function bindProduct(product) {
    if (boundProducts.has(product)) return;
    boundProducts.add(product);
    const form = product.querySelector('product-form form');
    if (!form) return;
    const button = () => form.querySelector('[name="add"]');
    const sizeGroup = () => product.querySelector('[data-rx-option="size"]');
    const sticky = document.querySelector(`[data-rx-sticky="${product.dataset.section}"]`) || document.querySelector('.rx-sticky-buy');
    const stickyButton = sticky?.querySelector('button');
    const stickyPrice = sticky?.querySelector('[data-rx-sticky-price]');
    let requiresChoice = product.dataset.rxRequireSize === 'true';
    const productAvailable = product.dataset.productAvailable !== 'false';
    const soldOutLabel = window.variantStrings?.soldOut || 'Sold out';
    let changingVariant = false;
    let restoringSize = false;
    const sizeScope = product.dataset.sizeScope;
    const sizeStatus = document.createElement('p');
    sizeStatus.className = 'rx-form-status'; sizeStatus.setAttribute('role', 'status');
    form.before(sizeStatus);
    function resetSize() {
      const group = sizeGroup();
      group?.querySelectorAll('input[type="radio"]').forEach(input => { input.checked = false; });
      const select = group?.querySelector('select');
      if (select) {
        select.querySelectorAll('option').forEach(option => option.removeAttribute('selected'));
        if (!select.querySelector('[data-rx-placeholder]')) {
          const placeholder = new Option('Choose size', '', true, true); placeholder.disabled = true; placeholder.dataset.rxPlaceholder = 'true'; select.prepend(placeholder);
        }
        select.value = '';
      }
    }
    function setButtonText(text) {
      const span = button()?.querySelector('span');
      if (span && span.textContent !== text) span.textContent = text;
    }
    function sync() {
      const mainButton = button();
      if (!mainButton) return;
      product.querySelectorAll('variant-selects [data-option-disclosure]').forEach(row => {
        const selected = row.querySelector('input:checked') || row.querySelector('select');
        const value = row.querySelector('[data-option-selection]');
        if (!value) return;
        const label = row.dataset.optionDisclosure === 'size' && requiresChoice ? 'Choose size' : selected?.value;
        if (label && value.textContent !== label) value.textContent = label;
      });
      if (requiresChoice || changingVariant) {
        const disabled = changingVariant || !productAvailable;
        if (mainButton.disabled !== disabled) mainButton.disabled = disabled;
        setButtonText(changingVariant ? 'Checking availability…' : productAvailable ? 'Choose size' : soldOutLabel);
      }
      if (stickyPrice) {
        const price = product.querySelector('[id^="price-"] .price');
        if (price && stickyPrice.innerHTML !== price.outerHTML) stickyPrice.innerHTML = price.outerHTML;
      }
      if (stickyButton) {
        const label = changingVariant ? 'Checking availability…' : requiresChoice && productAvailable ? 'Choose size' : mainButton.textContent.trim();
        if (stickyButton.textContent !== label) stickyButton.textContent = label;
        const disabled = !productAvailable || changingVariant || (!requiresChoice && (mainButton.disabled || mainButton.getAttribute('aria-disabled') === 'true'));
        if (stickyButton.disabled !== disabled) stickyButton.disabled = disabled;
      }
    }
    if (requiresChoice) resetSize();
    product.addEventListener('change', event => {
      if (!event.target.closest('variant-selects')) return;
      changingVariant = true;
      if (event.target.closest('[data-rx-option="size"]')) {
        requiresChoice = false; sizeStatus.textContent = '';
        if (!event.detail?.sizeMemoryRestore) {
          restoringSize = false;
          window.SizeMemory?.remember(sizeScope, event.target.value);
        }
      }
      sync();
    }, true);
    form.addEventListener('submit', event => {
      if (requiresChoice || changingVariant || button()?.disabled) {
        event.preventDefault(); event.stopImmediatePropagation();
        sizeStatus.textContent = !productAvailable ? soldOutLabel : requiresChoice ? 'Please choose your size.' : 'Please wait for the selected option to load.';
        if (sizeGroup()?.closest('[data-option-disclosure]')) sizeGroup().closest('[data-option-disclosure]').open = true;
        sizeGroup()?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        sizeGroup()?.querySelector('input,select')?.focus({ preventScroll: true });
      }
    }, true);
    stickyButton?.addEventListener('click', () => {
      if (requiresChoice) {
        if (sizeGroup()?.closest('[data-option-disclosure]')) sizeGroup().closest('[data-option-disclosure]').open = true;
        sizeGroup()?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        sizeGroup()?.querySelector('input,select')?.focus({ preventScroll: true });
      } else if (!button()?.disabled && !changingVariant) form.requestSubmit(button());
    });
    let scheduled = false;
    new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => { scheduled = false; if (requiresChoice) resetSize(); sync(); });
    }).observe(product, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled','aria-disabled'] });
    if (typeof subscribe === 'function') subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      if (data.sectionId !== product.dataset.section) return;
      if (restoringSize && !data.variant?.available) {
        requiresChoice = true;
        sizeStatus.textContent = 'Your saved size is unavailable for this selection. Please choose a size.';
      }
      restoringSize = false;
      changingVariant = false; if (requiresChoice) resetSize(); sync();
    });
    product.addEventListener('rulnx:variant-error', () => {
      changingVariant = false; restoringSize = false;
      if (sizeGroup()) { requiresChoice = true; resetSize(); }
      sizeStatus.textContent = 'Options could not load. Please select your size again.'; sync();
    });
    if (sticky) {
      let visibilityFrame = 0;
      const updateStickyVisibility = () => {
        visibilityFrame = 0;
        if (!product.isConnected) {
          window.removeEventListener('scroll', scheduleStickyVisibility);
          window.removeEventListener('resize', scheduleStickyVisibility);
          return;
        }
        const nativeButton = button();
        const headerBottom = Math.max(0, document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0);
        sticky.hidden = !nativeButton || window.innerWidth > 768 || nativeButton.getBoundingClientRect().bottom > headerBottom;
        if (nativeButton) sticky.style.setProperty('--purchase-button-width',nativeButton.getBoundingClientRect().width+'px');
      };
      const scheduleStickyVisibility = () => {
        if (!visibilityFrame) visibilityFrame = requestAnimationFrame(updateStickyVisibility);
      };
      window.addEventListener('scroll', scheduleStickyVisibility, {passive:true});
      window.addEventListener('resize', scheduleStickyVisibility, {passive:true});
      updateStickyVisibility();
    }
    sync();
    Promise.all(['variant-selects', 'product-info', 'product-form'].map(name => customElements.whenDefined(name))).then(() => {
      const params = new URL(window.location.href).searchParams;
      if (!product.isConnected || !requiresChoice || changingVariant || params.has('variant') || params.has('option_values')) return;
      const choice = window.SizeMemory?.option(sizeGroup(), window.SizeMemory?.get(sizeScope));
      if (!choice) return;
      restoringSize = true;
      const target = choice.tagName === 'OPTION' ? choice.closest('select') : choice;
      if (choice.tagName === 'OPTION') target.value = choice.value; else choice.checked = true;
      target.dispatchEvent(new CustomEvent('change', {bubbles:true, detail:{sizeMemoryRestore:true}}));
    });
  }
  function init() {
    updateHeader();
    document.querySelectorAll('[data-rx-main-product]').forEach(bindProduct);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('product-info:loaded', event => { if (event.target.matches('[data-rx-main-product]')) bindProduct(event.target); });
  const header = document.querySelector('.section-header');
  if (header && 'ResizeObserver' in window) new ResizeObserver(scheduleHeader).observe(header);
})();
